import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { Subscription } from 'rxjs';
import { TenantScopeService } from '../tenant-scope.service';
import { TenantScope, TenantScopeType } from '../tenant-access.model';
import { Company } from '../company.model';
import { SessionStorageService } from '../../services/session-storage.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

interface TenantScopeOption {
    label: string;
    shortLabel: string;
    hint: string;
    value: string;
    type: TenantScopeType;
    id: number;
    icon: string;
}

@Component({
    selector: 'app-tenant-scope-selector',
    standalone: true,
    imports: [CommonModule, FormsModule, SelectModule, TranslocoPipe],
    styles: [`
        :host {
            display: inline-flex;
            min-width: 0;
        }

        .tenant-scope-selector {
            display: inline-flex;
            min-width: 0;
        }

        .tenant-scope-static,
        :host ::ng-deep .tenant-scope-select.p-select {
            min-width: 18rem;
            max-width: 22rem;
            min-height: 42px;
            border: 1px solid #d9e2ec;
            border-radius: 10px;
            background: #ffffff;
            box-shadow: 0 1px 2px rgba(15, 23, 42, .04);
        }

        .tenant-scope-static {
            display: inline-flex;
            align-items: center;
            padding: .2rem .7rem;
        }

        :host ::ng-deep .tenant-scope-select.p-select {
            align-items: stretch;
        }

        :host ::ng-deep .tenant-scope-select .p-select-label {
            padding: 0;
        }

        :host ::ng-deep .tenant-scope-select .p-select-dropdown {
            width: 2.5rem;
            color: #64748b;
        }

        .tenant-scope-option {
            display: flex;
            align-items: center;
            gap: .72rem;
            min-width: 0;
            width: 100%;
            padding: .22rem 0;
        }

        .tenant-scope-avatar,
        .tenant-scope-icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 34px;
            width: 34px;
            height: 34px;
            border-radius: 10px;
            overflow: hidden;
            background: #eef2ff;
            color: #475569;
            font-size: .95rem;
            font-weight: 700;
        }

        .tenant-scope-logo {
            display: inline-flex;
            align-items: center;
            flex: 0 0 auto;
            min-width: 0;
        }

        .tenant-scope-logo img {
            display: block;
            width: auto;
            max-width: none;
            max-height: 34px;
            object-fit: cover;
        }

        .tenant-scope-avatar.fallback {
            background: #ecfeff;
            color: #0f766e;
        }

        .tenant-scope-icon.group {
            background: #ecfdf5;
            color: #0f766e;
        }

        .tenant-scope-copy {
            display: flex;
            flex-direction: column;
            min-width: 0;
            gap: .08rem;
        }

        .tenant-scope-hint {
            color: #94a3b8;
            font-size: .68rem;
            font-weight: 700;
            line-height: 1;
            text-transform: uppercase;
        }

        .tenant-scope-label {
            color: #172033;
            font-size: .84rem;
            font-weight: 700;
            line-height: 1.2rem;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        :host-context(.app-dark) .tenant-scope-static,
        :host-context(.app-dark) ::ng-deep .tenant-scope-select.p-select {
            background: #172033;
            border-color: #2a3950;
        }

        :host-context(.app-dark) ::ng-deep .tenant-scope-select .p-select-dropdown,
        :host-context(.app-dark) .tenant-scope-label {
            color: #dce7f3;
        }

        :host-context(.app-dark) .tenant-scope-hint {
            color: #9db0c6;
        }

        :host-context(.app-dark) .tenant-scope-avatar.fallback,
        :host-context(.app-dark) .tenant-scope-icon.group {
            background: rgba(45, 212, 191, .18);
            color: #99f6e4;
        }

        @media (max-width: 1200px) {
            .tenant-scope-static,
            :host ::ng-deep .tenant-scope-select.p-select {
                min-width: 15rem;
                max-width: 18rem;
            }
        }

        @media (max-width: 720px) {
            .tenant-scope-static,
            :host ::ng-deep .tenant-scope-select.p-select {
                min-width: 13rem;
                max-width: 15rem;
            }
        }
    `],
    template: `
        <div class="tenant-scope-selector" *ngIf="options.length > 1; else singleScope">
            <p-select
                [options]="options"
                [(ngModel)]="selectedValue"
                optionLabel="label"
                optionValue="value"
                appendTo="body"
                styleClass="tenant-scope-select"
                (onChange)="selectScope($event.value)">
                <ng-template pTemplate="selectedItem" let-option>
                    <span class="tenant-scope-option" *ngIf="option">
                        <ng-container *ngTemplateOutlet="scopeAvatar; context: { $implicit: option, selected: true }"></ng-container>
                        <span class="tenant-scope-copy">
                            <span class="tenant-scope-hint">{{ option.hint }}</span>
                            <span class="tenant-scope-label">{{ option.shortLabel }}</span>
                        </span>
                    </span>
                </ng-template>
                <ng-template pTemplate="item" let-option>
                    <span class="tenant-scope-option">
                        <ng-container *ngTemplateOutlet="scopeAvatar; context: { $implicit: option, selected: false }"></ng-container>
                        <span class="tenant-scope-copy">
                            <span class="tenant-scope-hint">{{ option.hint }}</span>
                            <span class="tenant-scope-label">{{ option.label }}</span>
                        </span>
                    </span>
                </ng-template>
            </p-select>
        </div>

        <ng-template #singleScope>
            <div class="tenant-scope-static" *ngIf="selectedOption as option">
                <span class="tenant-scope-option">
                    <ng-container *ngTemplateOutlet="scopeAvatar; context: { $implicit: option, selected: true }"></ng-container>
                    <span class="tenant-scope-copy">
                        <span class="tenant-scope-hint">{{ option.hint }}</span>
                        <span class="tenant-scope-label">{{ option.shortLabel }}</span>
                    </span>
                </span>
            </div>
        </ng-template>

        <ng-template #scopeAvatar let-option let-selected="selected">
            <ng-container *ngIf="option.type === 'COMPANY'; else groupAvatar">
                <span *ngIf="companyLogo(option) as resolvedCompanyLogo; else companyFallback" class="tenant-scope-logo">
                    <img [src]="resolvedCompanyLogo" [alt]="'tenant.companyLogo' | transloco">
                </span>
                <ng-template #companyFallback>
                    <span class="tenant-scope-avatar fallback">
                    <span *ngIf="!companyLogo(option)">{{ companyInitial(option) }}</span>
                    </span>
                </ng-template>
            </ng-container>
            <ng-template #groupAvatar>
                <span class="tenant-scope-icon group">
                    <i [class]="option.icon"></i>
                </span>
            </ng-template>
        </ng-template>
    `
})
export class TenantScopeSelectorComponent implements OnInit, OnDestroy {
    @Input() currentCompany: Company | null = null;
    @Output() scopeChange = new EventEmitter<TenantScope>();

    options: TenantScopeOption[] = [];
    selectedValue = '';

    private subscriptions = new Subscription();

    constructor(
        private tenantScopeService: TenantScopeService,
        private sessionStorageService: SessionStorageService,
        private transloco: TranslocoService
    ) {}

    ngOnInit(): void {
        this.subscriptions.add(this.tenantScopeService.accessSummary$.subscribe(() => this.rebuildOptions()));
        this.subscriptions.add(this.tenantScopeService.activeScope$.subscribe(scope => {
            this.selectedValue = scope ? this.scopeValue(scope) : '';
        }));
        this.subscriptions.add(this.transloco.langChanges$.subscribe(() => this.rebuildOptions()));
        this.rebuildOptions();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    get selectedOption(): TenantScopeOption | undefined {
        return this.options.find(item => item.value === this.selectedValue) ?? this.options[0];
    }

    selectScope(value: string): void {
        const option = this.options.find(item => item.value === value);
        if (!option) {
            return;
        }

        const changed = option.type === 'COMPANY'
            ? this.tenantScopeService.selectCompany(option.id)
            : this.tenantScopeService.selectCompanyGroup(option.id);

        const activeScope = this.tenantScopeService.getActiveScope();
        if (changed && activeScope) {
            this.scopeChange.emit(activeScope);
        }
    }

    private rebuildOptions(): void {
        const companies = this.tenantScopeService.getAccessibleCompanies().map(company => ({
            label: company.companyName,
            shortLabel: company.companyName,
            hint: this.transloco.translate('tenant.company'),
            value: `COMPANY:${company.companyId}`,
            type: 'COMPANY' as const,
            id: company.companyId,
            icon: 'pi pi-building'
        }));
        const groups = this.tenantScopeService.getAccessibleCompanyGroups().map(group => ({
            label: this.transloco.translate('tenant.allCompaniesLabel', { group: group.companyGroupName }),
            shortLabel: group.companyGroupName,
            hint: this.transloco.translate('tenant.companyGroup'),
            value: `GROUP:${group.companyGroupId}`,
            type: 'GROUP' as const,
            id: group.companyGroupId,
            icon: 'pi pi-sitemap'
        }));

        this.options = [...companies, ...groups];
        const activeScope = this.tenantScopeService.getActiveScope();
        this.selectedValue = activeScope ? this.scopeValue(activeScope) : this.options[0]?.value ?? '';
    }

    private scopeValue(scope: TenantScope): string {
        return scope.type === 'COMPANY'
            ? `COMPANY:${scope.companyId}`
            : `GROUP:${scope.companyGroupId}`;
    }

    companyLogo(option: TenantScopeOption): string | null {
        if (option.type !== 'COMPANY') {
            return null;
        }

        const storedCompany = this.sessionStorageService.getCompany();
        if (storedCompany?.id === option.id && storedCompany.logo) {
            return storedCompany.logo;
        }

        if (this.currentCompany?.id === option.id && this.currentCompany.logo) {
            return this.currentCompany.logo;
        }

        return null;
    }

    companyInitial(option: TenantScopeOption): string {
        const label = option.shortLabel?.trim() || option.label?.trim() || 'C';
        return label.charAt(0).toUpperCase();
    }
}
